<script>
export default {
  props: {
    accept: {
      type: String,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: [Array],
      default: () => [],
    },
    value: {
      type: [Array],
      default: () => [],
    },
    showDownload: {
      type: Boolean,
      default: false,
    },
    emptyMessage: {
      type: String,
      default: 'Nenhum arquivo',
    },
    hideUploadButton: {
      type: Boolean,
      default: false,
    },
    labelBold: {
      type: Boolean,
      default: false,
    },
    maxFileSize: {
      type: Number,
      default: null,
    },
  },
  data: function () {
    return {
      innerValue: this.value,
      hasError: false,
      errorMessage: ''
    };
  },
  computed: {
    activatorProps() {
      return {
        disabled: this.disabled,
        label: this.label,
        labelBold: this.labelBold,
        onclick: () => this.open(),
      };
    },
    selectedFiles: {
      get() {
        return this.innerValue;
      },
      set(files) {
        const safe = Array.from(files);
        this.innerValue = safe;
        this.$emit('update:value', this.innerValue);
      },
    },
    maxFileSizeHint() {
      if (!this.maxFileSize) return null;
      const sizeMB = (this.maxFileSize / (1024 * 1024)).toFixed(0);
      return `Tamanho máximo: ${sizeMB}MB por arquivo`;
    },
  },
  methods: {
    onInputChange(changeEvent) {
      const newFiles = Array.from(changeEvent.target.files);
      if (this.multiple && this.innerValue && this.innerValue.length > 0) {
        // Append new files to existing ones
        this.innerValue = [...this.innerValue, ...newFiles];
        this.$emit('update:value', this.innerValue);
      } else {
        this.selectedFiles = newFiles;
      }
      changeEvent.target.value = "";
      this.validate();
    },
    open() {
      this.$refs.input?.click();
    },
    removeFile(index) {
      if (this.disabled) return;
      if (this.selectedFiles[index]) {
        this.selectedFiles.splice(index, 1);
        this.validate();
      }
    },
    downloadFile(url) {
      return  window.open(url, '_blank');
    },
    validate() {
      this.hasError = false;
      this.errorMessage = '';

      // Validate max file size
      if (this.maxFileSize && this.innerValue) {
        for (const file of this.innerValue) {
          if (file.size && file.size > this.maxFileSize) {
            const maxSizeMB = (this.maxFileSize / (1024 * 1024)).toFixed(0);
            this.hasError = true;
            this.errorMessage = `Arquivo "${file.name || file.nome}" excede o limite de ${maxSizeMB}MB`;
            return false;
          }
        }
      }

      for (const rule of this.rules) {
        const result = rule(this.innerValue);

        if (typeof result === 'string') {
          this.hasError = true;
          this.errorMessage = result;
          break;
        } else if (result === false) {
          this.hasError = true;
          this.errorMessage = 'Valor inválido';
        }
      }

      return !this.hasError;
    }
  },
  watch: {
    value: {
      handler(val) {
        this.innerValue = val;
      },
      deep: true,
    },
  },
};
</script>

<template>
  <div :class="{ 'error': hasError }">
    <input
      ref="input"
      :accept="accept"
      :disabled="disabled"
      :multiple="multiple"
      type="file"
      @change="onInputChange"
    />
    <slot name="activator" :props="activatorProps">
      <span v-if="disabled && (!selectedFiles || selectedFiles.length === 0)" class="grey--text text--darken-1 pl-1">
        {{ emptyMessage }}
      </span>
      <span v-else-if="!hideUploadButton" :class="{'font-weight-medium': labelBold}">{{ label }}</span>
      <v-btn v-if="!(disabled && (!selectedFiles || selectedFiles.length === 0)) && !hideUploadButton" block depressed :disabled="disabled" @click="open" class="mt-2">
        <v-icon left>mdi-upload</v-icon>Selecionar arquivo
      </v-btn>
      <div v-if="maxFileSizeHint && !disabled" class="file-size-hint">
        {{ maxFileSizeHint }}
      </div>
    </slot>
    <slot :files="selectedFiles" v-if="selectedFiles && selectedFiles.length > 0">
      <v-card
        v-for="(file, key) in selectedFiles"
        :key="key"
        elevation="0"
        class="mt-2"
        outlined
      >
        <v-tooltip top>
          <template v-slot:activator="{on}">
            <v-card-text v-on="on">
              <span class="v-label-input text-truncate">
                <v-icon>mdi-file-document</v-icon>
                <span>{{ file.name || file.nome }}</span>
              </span>
              <div class="d-flex">
                <v-btn
                  v-if="showDownload && file.url"
                  small
                  icon
                  @click="downloadFile(file.url)"
                  color="info"
                  class="mr-1"
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
                <v-btn small icon @click="removeFile(key)" :disabled="disabled">
                  <v-icon>mdi-close-circle-outline</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </template>

          <span>{{ file.name || file.nome }}</span>
        </v-tooltip>
      </v-card>
    </slot>
    <slot :values="value" v-if="selectedFiles && selectedFiles.length == 0">
      <v-card
        v-for="(file, key) in value"
        :key="key"
        elevation="0"
        class="mt-2"
        outlined
      >
        <v-tooltip top>
          <template v-slot:activator="{on}">
            <v-card-text v-on="on">
              <span class="v-label-input text-truncate">
                <v-icon>mdi-file-document</v-icon>
                <span>{{ file.name || file.nome }}</span>
              </span>
              <div class="d-flex">
                <v-btn
                  v-if="showDownload && file.url"
                  small
                  icon
                  @click="downloadFile(file.url)"
                  color="info"
                >
                  <v-icon>mdi-download</v-icon>
                </v-btn>
              </div>
            </v-card-text>
          </template>

          <span>{{ file.name || file.nome }}</span>
        </v-tooltip>
      </v-card>
    </slot>
    <div v-if="hasError" class="error-message">
      {{ errorMessage }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
input[type="file"] {
  display: none;
}

.v-card__text {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  width: 100%;

  .v-label-input.text-truncate .v-icon {
    margin-right: .5rem;
  }
}

.error {
  border: thin solid red;
  background-color: white !important;
}

.error-message {
  color: red;
  font-size: 0.8rem;
  margin-top: 4px;
  margin-left: 10px;
}

.file-size-hint {
  color: #666;
  font-size: 0.75rem;
  margin-top: 4px;
  font-style: italic;
}
</style>
